import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { campaignPipelineAPIClient } from '@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient';
import { CAMPAIGN_PIPELINE_KEYS } from '@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys';
import { CAMPAIGN_ACTIVITY_KEYS } from '@/marketing/infrastructure/influencer/campaign/activity/utils/keys';
import { extractApiErrorMessage } from '@/marketing/lib/extractApiErrorMessage';
import type { CampaignInfluencer } from '@/marketing/models/campaignInfluencer';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { useCampaignInfluencerTagLookup } from './useCampaignInfluencerTagLookup';

export interface CampaignInfluencerAssignedTag {
  tagId: string;
  name: string;
  isGlobal: boolean;
}

export interface CampaignInfluencerTagAssignment {
  assignedTagIds: string[];
  assignedTags: CampaignInfluencerAssignedTag[];
  isAssignmentPending: boolean;
  toggleAssignment: (tagId: string) => void;
}

// Tag-assignment logic for a single enrollment, shared by the slide-over section and the kanban card.
export function useCampaignInfluencerTagAssignment(
  campaignId: string,
  influencer: CampaignInfluencer,
): CampaignInfluencerTagAssignment {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const transitionBy = currentUser?.email ?? 'unknown';
  const { tagNameById, globalTagIds, isReady } = useCampaignInfluencerTagLookup(campaignId);

  const rawAssignedTagIds = influencer.tags ?? [];
  const assignedTagIds = isReady ? rawAssignedTagIds.filter((tagId) => tagNameById.has(tagId)) : rawAssignedTagIds;

  const setTagsMutation = useMutation({
    mutationFn: (tagIds: string[]) =>
      campaignPipelineAPIClient.setCampaignInfluencerTags(campaignId, influencer.influencerId, {
        tagIds,
        transitionBy,
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.lists() }),
        queryClient.invalidateQueries({ queryKey: CAMPAIGN_ACTIVITY_KEYS.all }),
      ]),
    onError: (error: unknown) => toast.error(extractApiErrorMessage(error, 'Failed to update tags')),
  });

  const toggleAssignment = (tagId: string) => {
    const nextTagIds = assignedTagIds.includes(tagId)
      ? assignedTagIds.filter((id) => id !== tagId)
      : [...assignedTagIds, tagId];

    setTagsMutation.mutate(nextTagIds);
  };

  const assignedTags = assignedTagIds
    .map((tagId) => ({ tagId, name: tagNameById.get(tagId), isGlobal: globalTagIds.has(tagId) }))
    .filter((tag): tag is CampaignInfluencerAssignedTag => Boolean(tag.name));

  return {
    assignedTagIds,
    assignedTags,
    isAssignmentPending: setTagsMutation.isPending,
    toggleAssignment,
  };
}
